<!-- PaymentModal.vue -->
<template>
  <div class="modal fade" id="payment-modal" data-bs-backdrop="static" data-bs-keyboard="false" tabindex="-1" role="dialog" aria-labelledby="paymentModalLabel" aria-hidden="true">
    <div class="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable" role="document">
      <div class="modal-content">
        <div class="modal-header text-white">
          <h5 class="modal-title fw-bold" id="paymentModalLabel">
            <i class="mdi mdi-receipt me-2"></i>
            <span v-if="paymentPanel">Invoice Generated</span>
            <span v-else-if="paymentMethod">Payment Method</span>
            <span v-else-if="transactionSummary">Transaction Summary</span>
            <span v-else-if="transactionCompleted">Payment Complete</span>
          </h5>
          <button type="button" class="btn-close btn-close-black" data-bs-dismiss="modal" aria-label="Close"></button>
        </div>

        <div class="modal-body p-0">
          <!-- Invoice Details Panel -->
          <div v-if="paymentPanel" class="invoice-panel">
            <!-- Invoice Header -->
            <div class="bg-light border-bottom p-4">
              <div class="row align-items-center">
                <div class="col-md-8">
                  <div class="d-flex align-items-center">
                    <div class="success-icon me-3">
                      <i class="mdi mdi-check-circle display-4 text-success"></i>
                    </div>
                    <div>
                      <h5 class="fw-bold text-success mb-1">Invoice Generated Successfully!</h5>
                      <p class="text-muted mb-0">{{ message || 'Your invoice has been created and is ready for payment.' }}</p>
                    </div>
                  </div>
                </div>
                <div class="col-md-4 text-end">
                  <div class="invoice-status">
                    <span class="badge bg-warning text-dark fs-6 px-3 py-2">
                      <i class="mdi mdi-clock-outline me-1"></i>UNPAID
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <!-- Invoice Details -->
            <div class="p-4">
              <div class="invoice-details mb-4">
                <div class="row">
                  <!-- Left Column - Bill Information -->
                  <div class="col-md-6">
                    <div class="card border-0 shadow-sm h-100">
                      <div class="card-header bg-primary text-white">
                        <h6 class="mb-0 fw-bold" style="color: white">
                          <i class="mdi mdi-file-document-outline me-2"></i>Invoice Details
                        </h6>
                      </div>
                      <div class="card-body">
                        <div class="detail-row mb-3">
                          <span class="text-muted small fw-semibold">Invoice Number</span>
                          <span class="fw-bold text-primary d-block">{{ billDetails.billNo || 'N/A' }}</span>
                        </div>

                        <div class="detail-row mb-3">
                          <span class="text-muted small fw-semibold">Service Type</span>
                          <span class="fw-bold d-block">{{ billDetails.incomeTypeDescription || 'N/A' }}</span>
                        </div>

                        <div class="detail-row mb-3">
                          <span class="text-muted small fw-semibold">Fee Description</span>
                          <span class="d-block">{{ billDetails.feeDescription || 'N/A' }}</span>
                        </div>

                        <div class="detail-row mb-3">
                          <span class="text-muted small fw-semibold">Issue Date</span>
                          <span class="d-block">{{ formatDate(billDetails.dateCreated) }}</span>
                        </div>

                        <div class="detail-row mb-0">
                          <span class="text-muted small fw-semibold">Status</span>
                          <span class="d-block">
                            <span class="badge bg-warning text-dark">{{ billDetails.status || 'UNPAID' }}</span>
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- Right Column - Customer & Location -->
                  <div class="col-md-6">
                    <div class="card border-0 shadow-sm h-100">
                      <div class="card-header bg-info text-white">
                        <h6 class="mb-0 fw-bold" style="color: white">
                          <i class="mdi mdi-account-outline me-2"></i>Customer Details
                        </h6>
                      </div>
                      <div class="card-body">
                        <div class="detail-row mb-3">
                          <span class="text-muted small fw-semibold">Customer</span>
                          <span class="fw-bold d-block">{{ billDetails.customerName || 'N/A' }}</span>
                        </div>

                        <div class="detail-row mb-3">
                          <span class="text-muted small fw-semibold">Sub County</span>
                          <span class="d-block">{{ billDetails.subCountyName || 'N/A' }}</span>
                        </div>

                        <div class="detail-row mb-3">
                          <span class="text-muted small fw-semibold">Ward</span>
                          <span class="d-block">{{ billDetails.wardName || 'N/A' }}</span>
                        </div>

                        <div class="detail-row mb-3">
                          <span class="text-muted small fw-semibold">Zone</span>
                          <span class="d-block">{{ billDetails.zone || 'N/A' }}</span>
                        </div>

                        <div class="detail-row mb-0">
                          <span class="text-muted small fw-semibold">Department</span>
                          <span class="d-block">{{ billDetails.department || 'N/A' }}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Invoice Description -->
              <div class="invoice-description mb-4" v-if="billDetails.description">
                <div class="card border-0 bg-light">
                  <div class="card-body">
                    <h6 class="fw-bold text-dark mb-2">
                      <i class="mdi mdi-information-outline me-2"></i>Description
                    </h6>
                    <p class="mb-0 text-muted">{{ billDetails.description }}</p>
                  </div>
                </div>
              </div>

              <!-- Amount Summary -->
              <div class="amount-summary mb-4">
                <div class="card border-0 shadow-sm">
                  <div class="card-header bg-success text-white">
                    <h6 class="mb-0 fw-bold" style="color: white">
                      <i class="mdi mdi-currency-usd me-2"></i>Amount Summary
                    </h6>
                  </div>
                  <div class="card-body">
                    <div class="row text-center">
                      <div class="col-md-4">
                        <div class="amount-item">
                          <div class="text-muted small">Invoice Amount</div>
                          <div class="h4 fw-bold text-primary mb-0">
                            KES {{ numberFormat(billDetails.detailAmount || 0) }}
                          </div>
                        </div>
                      </div>
                      <div class="col-md-4">
                        <div class="amount-item border-start">
                          <div class="text-muted small">Amount Paid</div>
                          <div class="h4 fw-bold text-success mb-0">
                            KES {{ numberFormat(billDetails.receiptAmount || 0) }}
                          </div>
                        </div>
                      </div>
                      <div class="col-md-4">
                        <div class="amount-item border-start">
                          <div class="text-muted small">Outstanding Balance</div>
                          <div class="h4 fw-bold text-danger mb-0">
                            KES {{ numberFormat(billDetails.billBalance || 0) }}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Action Buttons -->
              <div class="action-buttons">
                <div class="row g-3">
                  <div class="col-md-6">
                    <button
                        @click="$emit('print-bill')"
                        type="button"
                        class="btn btn-outline-primary btn-lg w-100">
                      <i class="mdi mdi-printer me-2"></i>Print Invoice
                    </button>
                  </div>
<!--                  <div class="col-md-6 d-none">
                    <button
                        @click="$emit('show-payment-method')"
                        type="button"
                        class="btn btn-success btn-lg w-100">
                      <i class="mdi mdi-credit-card me-2"></i>Make Payment
                    </button>
                  </div>-->
                </div>
              </div>
            </div>
          </div>

          <!-- Payment Method Selection Panel -->
          <div v-if="paymentMethod" class="payment-method-panel p-4">
            <div class="text-center mb-4">
              <i class="mdi mdi-credit-card display-3 text-primary opacity-75"></i>
              <h5 class="fw-bold mt-3 mb-2">Select Payment Method</h5>
              <p class="text-muted">Choose how you would like to process this payment</p>
            </div>

            <!-- Payment Amount Display -->
            <div class="payment-amount-display mb-4">
              <div class="alert alert-info text-center">
                <h6 class="fw-bold mb-1">Amount to Pay</h6>
                <div class="display-6 fw-bold text-primary">KES {{ numberFormat(billDetails.billBalance || 0) }}</div>
                <small class="text-muted">Invoice No: {{ billDetails.billNo }}</small>
              </div>
            </div>

            <!-- Payment Methods -->
            <div class="accordion" id="paymentAccordion">
              <!-- M-Pesa Payment -->
              <div class="accordion-item border-0 shadow-sm mb-3">
                <h2 class="accordion-header">
                  <button
                      class="accordion-button fw-medium rounded-top"
                      type="button"
                      data-bs-toggle="collapse"
                      data-bs-target="#mpesaCollapse"
                      aria-expanded="true">
                    <div class="d-flex align-items-center">
                      <div class="payment-icon me-3">
                        <i class="mdi mdi-cellphone-android display-6 text-success"></i>
                      </div>
                      <div>
                        <div class="fw-bold text-success">M-PESA Payment</div>
                        <small class="text-muted">Pay via mobile money transfer</small>
                      </div>
                    </div>
                  </button>
                </h2>
                <div id="mpesaCollapse" class="accordion-collapse collapse show">
                  <div class="accordion-body">
                    <div class="alert alert-light">
                      <i class="mdi mdi-information-outline me-2"></i>
                      A payment request of <strong>KES {{ numberFormat(billDetails.billBalance || 0) }}</strong>
                      will be sent to the M-PESA number you enter below.
                    </div>

                    <div class="form-group mb-3">
                      <label class="form-label fw-semibold">M-PESA Phone Number</label>
                      <div class="input-group input-group-lg">
                        <span class="input-group-text">
                          <i class="mdi mdi-phone text-success"></i>
                        </span>
                        <input
                            v-model="phoneNumber"
                            type="tel"
                            class="form-control"
                            placeholder="e.g., 0712345678"
                            :class="{ 'is-invalid': errors.phoneNumber }">
                      </div>
                      <div v-if="errors.phoneNumber" class="invalid-feedback d-block">{{ errors.phoneNumber }}</div>
                      <small class="text-muted">Enter the phone number registered with M-PESA</small>
                    </div>

                    <button
                        @click="handlePaymentSummary('MPESA')"
                        type="button"
                        class="btn btn-success btn-lg w-100"
                        :disabled="!phoneNumber">
                      <i class="mdi mdi-send me-2"></i>Send Payment Request
                    </button>
                  </div>
                </div>
              </div>

              <!-- Bank Payment -->
              <div class="accordion-item border-0 shadow-sm">
                <h2 class="accordion-header">
                  <button
                      class="accordion-button fw-medium collapsed rounded-top"
                      type="button"
                      data-bs-toggle="collapse"
                      data-bs-target="#bankCollapse"
                      aria-expanded="false">
                    <div class="d-flex align-items-center">
                      <div class="payment-icon me-3">
                        <i class="mdi mdi-bank display-6 text-primary"></i>
                      </div>
                      <div>
                        <div class="fw-bold text-primary">Bank Payment</div>
                        <small class="text-muted">Confirm payment with bank receipt</small>
                      </div>
                    </div>
                  </button>
                </h2>
                <div id="bankCollapse" class="accordion-collapse collapse">
                  <div class="accordion-body">
                    <div class="alert alert-light">
                      <i class="mdi mdi-information-outline me-2"></i>
                      Enter the bank receipt number to confirm payment for this transaction.
                    </div>

                    <div class="form-group mb-3">
                      <label class="form-label fw-semibold">Bank Receipt Number</label>
                      <div class="input-group input-group-lg">
                        <span class="input-group-text">
                          <i class="mdi mdi-receipt text-primary"></i>
                        </span>
                        <input
                            v-model="bankReceiptNo"
                            type="text"
                            class="form-control"
                            placeholder="Enter receipt number"
                            :class="{ 'is-invalid': errors.bankReceiptNo }">
                      </div>
                      <div v-if="errors.bankReceiptNo" class="invalid-feedback d-block">{{ errors.bankReceiptNo }}</div>
                      <small class="text-muted">Receipt number from your bank transaction</small>
                    </div>

                    <button
                        @click="handlePaymentSummary('BANK')"
                        type="button"
                        class="btn btn-primary btn-lg w-100"
                        :disabled="!bankReceiptNo">
                      <i class="mdi mdi-check me-2"></i>Confirm Bank Payment
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Transaction Summary Panel -->
          <div v-if="transactionSummary" class="transaction-summary-panel p-4">
            <div class="text-center mb-4">
              <i class="mdi mdi-file-check display-3 text-warning"></i>
              <h5 class="fw-bold mt-3 mb-2">Confirm Payment Details</h5>
              <p class="text-muted">Please review the transaction details before proceeding</p>
            </div>

            <div class="summary-card">
              <div class="card border-0 shadow-sm">
                <div class="card-header bg-light">
                  <h6 class="mb-0 fw-bold">
                    <i class="mdi mdi-file-document-outline me-2"></i>Transaction Summary
                  </h6>
                </div>
                <div class="card-body">
                  <div class="row g-3">
                    <div class="col-md-6">
                      <div class="summary-item">
                        <span class="text-muted small">Invoice Number</span>
                        <span class="fw-bold d-block">{{ billDetails.billNo }}</span>
                      </div>
                    </div>
                    <div class="col-md-6">
                      <div class="summary-item">
                        <span class="text-muted small">Service Type</span>
                        <span class="fw-bold d-block">{{ billDetails.incomeTypeDescription }}</span>
                      </div>
                    </div>
                    <div class="col-md-6">
                      <div class="summary-item">
                        <span class="text-muted small">Payment Method</span>
                        <span class="fw-bold d-block">
                          <span class="badge" :class="paymentMode === 'MPESA' ? 'bg-success' : 'bg-primary'">
                            {{ paymentMode }}
                          </span>
                        </span>
                      </div>
                    </div>
                    <div class="col-md-6">
                      <div class="summary-item">
                        <span class="text-muted small">Amount</span>
                        <span class="fw-bold text-success d-block h5 mb-0">
                          KES {{ numberFormat(billDetails.billBalance) }}
                        </span>
                      </div>
                    </div>
                  </div>

                  <hr class="my-3">

                  <!-- Payment Method Specific Details -->
                  <div v-if="paymentMode === 'MPESA'" class="payment-details">
                    <div class="alert alert-success">
                      <h6 class="fw-bold mb-2">
                        <i class="mdi mdi-cellphone-android me-2"></i>M-PESA Payment Request
                      </h6>
                      <p class="mb-2">
                        A payment request of <strong>KES {{ numberFormat(billDetails.billBalance) }}</strong>
                        will be sent to phone number <strong>{{ phoneNumber }}</strong>.
                      </p>
                      <p class="mb-0 small">
                        <i class="mdi mdi-information-outline me-1"></i>
                        Please check your phone and enter your M-PESA PIN to complete the payment.
                      </p>
                    </div>
                  </div>

                  <div v-if="paymentMode === 'BANK'" class="payment-details">
                    <div class="alert alert-primary">
                      <h6 class="fw-bold mb-2">
                        <i class="mdi mdi-bank me-2"></i>Bank Payment Confirmation
                      </h6>
                      <p class="mb-2">
                        Payment will be confirmed using bank receipt number: <strong>{{ bankReceiptNo }}</strong>
                      </p>
                      <p class="mb-0 small">
                        <i class="mdi mdi-information-outline me-1"></i>
                        Please ensure the receipt number is correct before proceeding.
                      </p>
                    </div>
                  </div>

                  <!-- Processing Message -->
                  <div v-if="message" class="alert alert-info text-center">
                    <i class="mdi mdi-loading mdi-spin me-2"></i>{{ message }}
                  </div>

                  <!-- Action Button -->
                  <div class="text-center mt-4">
                    <button
                        @click="$emit('make-payment', getPaymentData())"
                        class="btn btn-lg w-100"
                        :class="paymentMode === 'MPESA' ? 'btn-success' : 'btn-primary'"
                        :disabled="isProcessing">
                      <span v-if="isProcessing">
                        <span class="spinner-border spinner-border-sm me-2"></span>
                        Processing...
                      </span>
                      <span v-else>
                        <i :class="paymentMode === 'MPESA' ? 'mdi mdi-send' : 'mdi mdi-check'" class="me-2"></i>
                        {{ paymentMode === 'MPESA' ? 'Send Payment Request' : 'Confirm Payment' }}
                      </span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Transaction Completed Panel -->
          <div v-if="transactionCompleted" class="completion-panel p-4 text-center">
            <div class="success-animation mb-4">
              <i class="mdi mdi-check-circle display-1 text-success"></i>
            </div>

            <h4 class="fw-bold text-success mb-3">Payment Successful!</h4>

            <div class="completion-details mb-4">
              <div class="alert alert-success">
                <div class="row text-center">
                  <div class="col-md-6">
                    <div class="completion-item">
                      <small class="text-muted">Amount Paid</small>
                      <div class="h4 fw-bold text-success mb-0">
                        KES {{ numberFormat(receiptDetails.receiptAmount || 0) }}
                      </div>
                    </div>
                  </div>
                  <div class="col-md-6">
                    <div class="completion-item">
                      <small class="text-muted">Invoice Number</small>
                      <div class="h6 fw-bold mb-0">{{ receiptDetails.billNo }}</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <p class="text-muted mb-4">
              Your payment has been processed successfully. You can now print your receipt for your records.
            </p>

            <button
                @click="$emit('print-receipt')"
                type="button"
                class="btn btn-success btn-lg">
              <i class="mdi mdi-printer me-2"></i>Print Receipt
            </button>
          </div>
        </div>

        <!-- Modal Footer -->
        <div class="modal-footer bg-light" v-if="!transactionCompleted">
          <button type="button" class="btn btn-outline-secondary" data-bs-dismiss="modal">
            <i class="mdi mdi-close me-1"></i>Close
          </button>

          <button
              v-if="paymentMethod || transactionSummary"
              @click="goBack"
              type="button"
              class="btn btn-secondary">
            <i class="mdi mdi-arrow-left me-1"></i>Back
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'PaymentModal',
  props: {
    paymentPanel: {
      type: Boolean,
      default: true
    },
    paymentMethod: {
      type: Boolean,
      default: false
    },
    transactionSummary: {
      type: Boolean,
      default: false
    },
    transactionCompleted: {
      type: Boolean,
      default: false
    },
    billDetails: {
      type: Object,
      default: () => ({})
    },
    receiptDetails: {
      type: Object,
      default: () => ({})
    },
    message: {
      type: String,
      default: ''
    }
  },
  emits: ['show-payment-method', 'print-bill', 'make-payment', 'print-receipt', 'show-transaction-summary'],
  data() {
    return {
      phoneNumber: '',
      bankReceiptNo: '',
      paymentMode: '',
      isProcessing: false,
      errors: {}
    }
  },
  methods: {
    formatDate(dateString) {
      if (!dateString) return 'N/A';

      try {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-GB', {
          day: '2-digit',
          month: 'short',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        });
      } catch (error) {
        return dateString;
      }
    },

    numberFormat(num) {
      const britishNumberFormatter = new Intl.NumberFormat("en-GB");
      return britishNumberFormatter.format(num || 0);
    },

    validatePhoneNumber(phone) {
      const phoneRegex = /^(\+254|254|0)[7][0-9]{8}$/;
      return phoneRegex.test(phone.replace(/\s/g, ''));
    },

    handlePaymentSummary(mode) {
      this.errors = {};
      this.paymentMode = mode;

      if (mode === 'MPESA') {
        if (!this.phoneNumber) {
          this.errors.phoneNumber = 'Phone number is required';
          return;
        }
        if (!this.validatePhoneNumber(this.phoneNumber)) {
          this.errors.phoneNumber = 'Please enter a valid Kenyan phone number';
          return;
        }
      }

      if (mode === 'BANK') {
        if (!this.bankReceiptNo) {
          this.errors.bankReceiptNo = 'Bank receipt number is required';
          return;
        }
        if (this.bankReceiptNo.length < 3) {
          this.errors.bankReceiptNo = 'Receipt number seems too short';
          return;
        }
      }

      this.$emit('show-transaction-summary', {
        paymentMode: mode,
        phoneNumber: this.phoneNumber,
        bankReceiptNo: this.bankReceiptNo
      });
    },

    getPaymentData() {
      this.isProcessing = true;

      return {
        paymentMode: this.paymentMode,
        phoneNumber: this.phoneNumber,
        bankReceiptNo: this.bankReceiptNo,
        amount: this.billDetails.billBalance,
        accNo: this.billDetails.billNo
      };
    },

    goBack() {
      if (this.transactionSummary) {
        this.$emit('show-payment-method');
      } else if (this.paymentMethod) {
        // Go back to invoice panel - this would need to be handled by parent
        this.resetPaymentData();
      }
    },

    resetPaymentData() {
      this.phoneNumber = '';
      this.bankReceiptNo = '';
      this.paymentMode = '';
      this.isProcessing = false;
      this.errors = {};
    }
  },

  watch: {
    transactionCompleted(newVal) {
      if (newVal) {
        this.isProcessing = false;
      }
    }
  }
}
</script>

<style scoped>
.detail-row {
  margin-bottom: 1rem;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid #f8f9fa;
}

.detail-row:last-child {
  border-bottom: none;
  margin-bottom: 0;
  padding-bottom: 0;
}

.amount-item {
  padding: 1rem;
}

.summary-item {
  margin-bottom: 1rem;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid #f8f9fa;
}

.summary-item:last-child {
  border-bottom: none;
  margin-bottom: 0;
  padding-bottom: 0;
}

.payment-icon {
  opacity: 0.8;
}

.accordion-button:not(.collapsed) {
  background-color: #f8f9fa;
  border-color: #dee2e6;
}

.accordion-button:focus {
  box-shadow: none;
  border-color: #dee2e6;
}

.success-animation {
  animation: pulse 2s infinite;
}

@keyframes pulse {
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.05);
  }
  100% {
    transform: scale(1);
  }
}

.completion-item {
  padding: 0.5rem;
}

.border-start {
  border-left: 1px solid #dee2e6 !important;
}

@media (max-width: 768px) {
  .border-start {
    border-left: none !important;
    border-top: 1px solid #dee2e6 !important;
  }

  .amount-item,
  .completion-item {
    padding: 0.5rem;
    text-align: center;
  }
}

.mdi-loading {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}
</style>